import type { UIComponent } from './types';

export type ServerUIComponentType = Exclude<UIComponent['type'], '__validation_error__'>;

export type ComponentCatalogGroup = 'primitive' | 'layout' | 'form' | 'scene' | 'generic' | 'chart';

export interface ComponentCatalogEntry<TType extends ServerUIComponentType = ServerUIComponentType> {
  type: TType;
  group: ComponentCatalogGroup;
  description: string;
  acceptsChildren?: boolean;
}

export type ComponentCatalog = {
  [TType in ServerUIComponentType]: ComponentCatalogEntry<TType>;
};

export const ARI_ASSISTANT_UI_COMPONENT_CATALOG = {
  text: {
    type: 'text',
    group: 'primitive',
    description: 'Inline text rendered with optional emphasis styling.',
  },
  image: {
    type: 'image',
    group: 'primitive',
    description: 'Small visual image primitive.',
  },
  icon: {
    type: 'icon',
    group: 'primitive',
    description: 'Iconify icon primitive.',
  },
  avatar: {
    type: 'avatar',
    group: 'primitive',
    description: 'Person or entity avatar with optional Microsoft Graph photo resolution.',
  },
  button: {
    type: 'button',
    group: 'primitive',
    description: 'Display-only action button payload.',
  },
  card: {
    type: 'card',
    group: 'layout',
    description: 'Card layout with optional title, description, leading, and trailing content.',
    acceptsChildren: true,
  },
  list: {
    type: 'list',
    group: 'layout',
    description: 'Responsive list/grid container for UI components.',
    acceptsChildren: true,
  },
  column: {
    type: 'column',
    group: 'layout',
    description: 'Column container for UI components.',
    acceptsChildren: true,
  },
  app: {
    type: 'app',
    group: 'layout',
    description: 'Top-level app container for composed assistant UI payloads.',
    acceptsChildren: true,
  },
  'contact-card': {
    type: 'contact-card',
    group: 'scene',
    description: 'Single contact summary card with backwards-compatible contact fields.',
  },
  'contact-list': {
    type: 'contact-list',
    group: 'scene',
    description: 'Contact search/list result layout.',
  },
  'contact-list-item': {
    type: 'contact-list-item',
    group: 'scene',
    description: 'Single contact row used in contact lists.',
  },
  'email-layout': {
    type: 'email-layout',
    group: 'scene',
    description: 'Detailed email view.',
  },
  'capture-grid-layout': {
    type: 'capture-grid-layout',
    group: 'scene',
    description:
      "The user's own photographs and videos shown as a numbered grid. Tiles are numbered so the user can say which one to open.",
  },
  'capture-detail-layout': {
    type: 'capture-detail-layout',
    group: 'scene',
    description: 'One capture shown full size, with badges for a picture Ari took or edited.',
  },
  'skill-media-layout': {
    type: 'skill-media-layout',
    group: 'scene',
    description: "One skill step's picture, video or web video, shown as large as the screen allows.",
  },
  'document-results-layout': {
    type: 'document-results-layout',
    group: 'scene',
    description:
      'The documents a search found, best first, from Ari Cloud and Microsoft 365 together. Each row shows the file, its folder and place, a snippet, and the connector it came from.',
  },
  'document-source-layout': {
    type: 'document-source-layout',
    group: 'scene',
    description:
      'One source at the exact place the answer came from: a page with the passage marked, a cell range, numbered lines, or a transcript.',
  },
  'document-folder-layout': {
    type: 'document-folder-layout',
    group: 'scene',
    description:
      "A folder of the user's documents as a numbered list, folders first. The top of the documents and every document of one kind use it too.",
  },
  'email-list-layout': {
    type: 'email-list-layout',
    group: 'scene',
    description: 'Email inbox/search result layout.',
  },
  'copilot-answer-layout': {
    type: 'copilot-answer-layout',
    group: 'scene',
    description: 'Microsoft Copilot answer — one section of prose at a time, plus the flat source list behind it.',
  },
  'confirm-action-layout': {
    type: 'confirm-action-layout',
    group: 'scene',
    description: 'Post-confirmation result overlay for confirmed actions (email and Teams chat).',
  },
  'email-confirm-action-layout': {
    type: 'email-confirm-action-layout',
    group: 'scene',
    description: 'Email send confirmation popup — prompt while pending, outcome icon once resolved.',
  },
  'confirmation-popup-layout': {
    type: 'confirmation-popup-layout',
    group: 'scene',
    description: 'Generic action confirmation popup (e.g. delete draft) — prompt while pending, outcome icon once resolved.',
  },
  'calendar-confirm-action-layout': {
    type: 'calendar-confirm-action-layout',
    group: 'scene',
    description:
      'Calendar event creation confirmation popup — prompt while pending, "Event Created!" icon once resolved.',
  },
  'teams-message-bubble': {
    type: 'teams-message-bubble',
    group: 'scene',
    description: 'Single Microsoft Teams message bubble.',
  },
  'teams-draft-bubble': {
    type: 'teams-draft-bubble',
    group: 'scene',
    description:
      'Outgoing Teams draft bubble shown while the user is dictating, or marked failed when a send was rejected.',
  },
  'teams-chat': {
    type: 'teams-chat',
    group: 'scene',
    description: 'Microsoft Teams chat transcript.',
  },
  'teams-chat-list': {
    type: 'teams-chat-list',
    group: 'scene',
    description: 'Microsoft Teams chat list with optional provider header.',
  },
  'teams-chat-list-item': {
    type: 'teams-chat-list-item',
    group: 'scene',
    description: 'Single Microsoft Teams chat list row.',
  },
  'calendar-list': {
    type: 'calendar-list',
    group: 'scene',
    description: 'Outlook Calendar list with optional day/week/month tabs.',
  },
  'calendar-event': {
    type: 'calendar-event',
    group: 'scene',
    description: 'Single calendar event row used inside a calendar list.',
  },
  'calendar-event-detail': {
    type: 'calendar-event-detail',
    group: 'scene',
    description: 'Detailed view of a single calendar event with attendees.',
  },
  'calendar-event-draft': {
    type: 'calendar-event-draft',
    group: 'scene',
    description: 'Draft preview of a calendar event during HITL confirmation.',
  },
  'generic-list': {
    type: 'generic-list',
    group: 'generic',
    description: 'Generic list layout for typed generic items.',
    acceptsChildren: true,
  },
  'generic-detail': {
    type: 'generic-detail',
    group: 'generic',
    description: 'Generic detail layout for typed generic sections.',
    acceptsChildren: true,
  },
  'generic-grid': {
    type: 'generic-grid',
    group: 'generic',
    description: 'Generic grid layout for typed generic cards.',
    acceptsChildren: true,
  },
  bar_chart: {
    type: 'bar_chart',
    group: 'chart',
    description: 'ApexCharts-backed bar chart payload.',
  },
  line_chart: {
    type: 'line_chart',
    group: 'chart',
    description: 'ApexCharts-backed line chart payload.',
  },
  pie_chart: {
    type: 'pie_chart',
    group: 'chart',
    description: 'ApexCharts-backed pie chart payload.',
  },
  text_input: {
    type: 'text_input',
    group: 'form',
    description: 'Display-only text input field.',
  },
  textarea: {
    type: 'textarea',
    group: 'form',
    description: 'Display-only multiline text field.',
  },
  number_input: {
    type: 'number_input',
    group: 'form',
    description: 'Display-only number input field.',
  },
  checkbox: {
    type: 'checkbox',
    group: 'form',
    description: 'Display-only checkbox field.',
  },
  select: {
    type: 'select',
    group: 'form',
    description: 'Display-only select field.',
  },
  radio_group: {
    type: 'radio_group',
    group: 'form',
    description: 'Display-only radio group field.',
  },
  date_picker: {
    type: 'date_picker',
    group: 'form',
    description: 'Display-only date picker field.',
  },
  form: {
    type: 'form',
    group: 'form',
    description: 'Display-only form layout.',
    acceptsChildren: true,
  },
  form_errors: {
    type: 'form_errors',
    group: 'form',
    description: 'Validation errors for a display-only form.',
  },
} as const satisfies ComponentCatalog;

export const ARI_ASSISTANT_UI_COMPONENT_TYPES = Object.freeze(
  Object.keys(ARI_ASSISTANT_UI_COMPONENT_CATALOG) as ServerUIComponentType[]
);
